import Preloader from '../../../../../ui/svg/common/preloader';
import React, { useEffect, useState } from 'react';
import {
    rootActions,
    useAppDispatch,
    useAppSelector
} from '../../data/store';
import { getUTMFromStorage } from '../../../../../domain/marketing/utm/utm-provider';
import {
    CartDataValidationCode,
    ICartData,
    IConfirmOrderWithoutLeftSideResponse,
    IConfirmOrderWithoutLeftSideRequest,
} from '../../interfaces';
import { confirmOrderWithoutLeftSide } from '../../data/data-service';
import { IResponse } from '../../../../../interfaces/common-interfaces';
import { DEFAULT_ERROR } from '../../../../../DAL/data-provider';
import { ConfirmOrderResultErrorCodeEnum } from '../../enums/confirm-order/confirm-order-result-error-enum';
import { InsertOrderErrorCodeEnum } from '../../enums/confirm-order/insert-order-error-enum';
import { CompleteOrderResultErrorCodeEnum } from '../../enums/confirm-order/complete-order-result-error-code-enum';
import { removeCartDataStorage } from '../../data/cart-storage-provider';
import {
    redirectToThankYouPage
} from '../../../cart-shared-bridger-pay/domain/bridger-pay-provider';
import { outbrainPurchaseEvent } from '../../../../../domain/marketing/outbrain-provider';
import { CONFIRM_ORDER_BTN_STOP_LOADING_EVENT, ErrorMode } from './confirm-order-btn';
import { getConfirmButtonText } from '../../domain/shopping-cart-provider';

const ConfirmOrderBtnWithoutLeftSide = () => {

    const dispatch = useAppDispatch();

    const [ loading, setLoading ] = useState(false);
    const [ serverError, setServerError ] = useState('');
    const [ errorMode, setErrorMode ] = useState<ErrorMode>(ErrorMode.Error);

    const cartData = useAppSelector(store => store.root.cartData);
    const isSbsAgreeCheckboxChecked = useAppSelector(store => store.root.isSbsAgreeCheckboxChecked);

    const isInAppPurchase = useAppSelector(store => store.root.isInAppPurchase);

    useEffect(() => {

        const stopLoading = (evt: CustomEvent) => {
            const error = evt?.detail?.error || '';
            setLoading(false);

            if(error) {
                setServerError(error);
            }
        };

        // @ts-ignore
        document.addEventListener(CONFIRM_ORDER_BTN_STOP_LOADING_EVENT, stopLoading);

        return () => {
            // @ts-ignore
            document.removeEventListener(CONFIRM_ORDER_BTN_STOP_LOADING_EVENT, stopLoading);
        };

    }, [ ]);

    // ------------------------- SERVER RESPONSE ------------------------------

    const handleConfirmOrderResponse = (
        confirmOrderResponse: IResponse,
        setServerError: Function,
        setCartData: Function,
        isInAppPurchase: boolean,
        dispatch: Function,
    ) => {
        let isResponseValid = confirmOrderResponse && confirmOrderResponse.statusCode === 200;

        const hasModelValidationError =
            isResponseValid &&
            confirmOrderResponse &&
            confirmOrderResponse.data &&
            confirmOrderResponse.data.errors &&
            confirmOrderResponse.data.errors.length > 0;

        // show the first error from .net model state validation
        if(hasModelValidationError){
            // @ts-ignore - I check if errors list exists above
            setServerError(confirmOrderResponse.data.errors[0]);
            return false;
        }

        // something went wrong - there is no content in the response
        if(!confirmOrderResponse.data || !confirmOrderResponse.data.relatedData){
            setServerError(DEFAULT_ERROR);
            return false;
        }

        // make sure we take 'data.isValid' into account
        if(isResponseValid){
            isResponseValid = confirmOrderResponse.data.isValid;
        }

        const result = confirmOrderResponse.data.relatedData as IConfirmOrderWithoutLeftSideResponse;
        
        // make sure we take 'result.isValid' into account
        if(isResponseValid){
            isResponseValid = result.isValid;
        }

        // handle error message from BrigerPay that went through custom table filter
        if(!isResponseValid && result.zoozErrorMessage){
            setServerError(result.zoozErrorMessage);
            return false;
        }

        // general error messages from server, like zip code for USA, etc.
        if(!isResponseValid && result.errorMessage){
            setServerError(result.errorMessage);
            return false;
        }

        // here it's possible to check each of the errors by the status codes
        // result.errorCode, result.insertOrderError, result.completeOrderError
        // but most of such errors can't be shown to user ---> just general default message should be shown

        // the 'total has changed' case
        if(result.errorCode === ConfirmOrderResultErrorCodeEnum.TotalPriceChanged){
            setServerError('Your shopping cart total has changed since you last reviewed it.');
            setErrorMode(ErrorMode.Warning);

            if(result.cartData){
                setCartData(result.cartData);
            }
            return false;
        }

        // the 'is fraud' case
        if(result.insertOrderError === InsertOrderErrorCodeEnum.IsFraudError ||
            result.insertOrderError === InsertOrderErrorCodeEnum.FraudError101 ||
            result.insertOrderError === InsertOrderErrorCodeEnum.FraudError102){
            setServerError('<p>You may not continue with your purchase due to too many attempts. For more details, <a href="/contact-orders" title="">contact Waves Orders Team</a>.</p>');

            if(result.cartData){
                setCartData(result.cartData);
            }
            return false;
        }

        if(result.insertOrderError === InsertOrderErrorCodeEnum.FraudError103){
            setServerError('<p>You may not continue with your purchase as your credit card was flagged as potential fraud. For more details, <a href="/contact-orders" title="">contact Waves Orders Team</a>.</p>');

            if(result.cartData){
                setCartData(result.cartData);
            }
            return false;
        }

        if(result.insertOrderError === InsertOrderErrorCodeEnum.FraudErrorDisposableEmail){
            setServerError('<p>Your purchase cannot be completed because the provided username or email address is temporary or disposable.</p>');

            if(result.cartData){
                setCartData(result.cartData);
            }
            return false;
        }

        if(result.insertOrderError === InsertOrderErrorCodeEnum.FraudError200){
            setServerError('<p>You may not continue with your purchase as your account was flagged as potential fraud. For more details, <a href="/contact-orders" title="">contact Waves Orders Team</a>.</p>');

            if(result.cartData){
                setCartData(result.cartData);
            }
            return false;
        }

        if(result.completeOrderError === CompleteOrderResultErrorCodeEnum.InsertSubscriptionOrderError){
            removeCartDataStorage();

            // redirect: account has been charged
            window.location.href = isInAppPurchase ? '/inapp/cart/error?errorId=97' : '/cart/error?errorId=97';
            return true;
        }

        if(result.insertOrderError === InsertOrderErrorCodeEnum.IsEligibleWCSSBSError){
            setServerError('<p>You may not purchase this Subscription.</p>');

            if(result.cartData){
                setCartData(result.cartData);
            }
            return false;
        }

        // if we arrived here, and the response is not valid ---> there is an unknown error
        if(!isResponseValid){
            setServerError(DEFAULT_ERROR);
            return false;
        }

        // all is well, the purchase went OK -----------------------

        // Send marketing purchase events.
        outbrainPurchaseEvent();
       /* sendTiktokPlaceAnOrder('Purchase', cartData);
        sendTiktokPlaceAnOrder('Purchase VRG Free', cartData);*/

        // in case of PayPal or AliPay ---> redirect
        if(result.redirect){
            window.location.href = result.redirect;
            return true;
        }

        redirectToThankYouPage(result?.orderGUID ?? '', isInAppPurchase);

        return true;
    };

    // ------------------------- PROCEED TO CHECKOUT ------------------------------

    const proceedHandlerHelper = async () : Promise<boolean> => {

        // https://dev13.waves.com/cart?utm_source=aaa&utm_medium=bbb&utm_campaign=ddd
        const utm = getUTMFromStorage();

        const confirmOrderModel: IConfirmOrderWithoutLeftSideRequest = {
            lastUTMSource: utm ? utm.lastSource : '',
            lastUTMMedium: utm ? utm.lastMedium : '',
            lastUTMCampaign: utm ? utm.lastCampaign : '',
        };

        setServerError('');
        setErrorMode(ErrorMode.Error);

        const response = await confirmOrderWithoutLeftSide(confirmOrderModel);

        // Server returns different status codes;
        // Each status code or error code may cause its own behaviour.
        const hasRedirect = handleConfirmOrderResponse(
            response,
            setServerError,
            (updatedCartData: ICartData) => {
                dispatch(
                    rootActions.setCartData(updatedCartData)
                );
            },
            isInAppPurchase,
            dispatch
        );

        if(!hasRedirect){
            setLoading(false);
        }

        return true;
    };

    const proceedHandler = async () => {
        const _confirmButtonDisabled = isConfirmButtonDisabled();
        if(_confirmButtonDisabled) {
            if(cartData?.validationCode === CartDataValidationCode.CreditsEqualPlansError ||
                cartData?.validationCode === CartDataValidationCode.CreditsEqualPlansBuyCreditsError) return;
            setServerError('Please agree to the terms.');
            return;
        }

        setLoading(true);
        const isValid = await proceedHandlerHelper();

        if(!isValid) {
            setLoading(false);
        }
    };

    /**
     * https://wavesaudio.atlassian.net/browse/DSWEB-4470 ----------------------------------------
     */
    const onCreditCheckboxChange = () => {
        setServerError('');

        dispatch(
            rootActions.main({
                isSbsAgreeCheckboxChecked: !isSbsAgreeCheckboxChecked,
            })
        );
    };

    const isConfirmButtonDisabled = () : boolean => {

        // https://wavesaudio.atlassian.net/browse/DSWEB-4475
        if(cartData?.validationCode === CartDataValidationCode.CreditsEqualPlansError ||
            cartData?.validationCode === CartDataValidationCode.CreditsEqualPlansBuyCreditsError) return true;

        return !isSbsAgreeCheckboxChecked;
    };

    const confirmButtonDisabled = isConfirmButtonDisabled();

    return (
        <>
            <label className="flex items-center mb-4">
                <input
                    className="mr-2"
                    type="checkbox"
                    checked={ isSbsAgreeCheckboxChecked }
                    onChange={ onCreditCheckboxChange }
                />
                <span>I agree to the terms above</span>
            </label>

            {
                !loading &&
                <button
                    type="button"
                    className={ `w-btn text-xl font-normal py-3 ${ confirmButtonDisabled ? 'bg-gray-400' : '' }` }
                    aria-disabled={ confirmButtonDisabled }
                    data-qa="cart-step3-confirm-order"
                    onClick={ proceedHandler }>
                    { getConfirmButtonText(cartData) }
                </button>
            }
            {
                loading &&
                <div className="flex justify-center my-6" data-qa="cart-step3-order-process-preloader">
                    <Preloader width="30px" height="30px" color="#4f4f4f"/>
                </div>
            }

            {
                serverError && <div
                    role="alert"
                    className={ `my-4 break-words ${ errorMode === ErrorMode.Warning ? 'info-box' : 'error-box' }` }
                    data-qa="cart-step3-confirm-order-error"
                    dangerouslySetInnerHTML={ {__html: serverError} }/>
            }

            {
                (cartData?.validationCode === CartDataValidationCode.CreditsEqualPlansError) &&
                <div
                    role="alert"
                    className="error-box mt-4">
                    <p>You are already subscribed to this plan. If you need more credits, you can <a
                        href="/voice-regen/pricing">subscribe to a bigger plan</a>, or <a href="/voice-regen/pricing">purchase
                        standalone credit packs</a>.</p>
                </div>
            }

            {
                (cartData?.validationCode === CartDataValidationCode.CreditsBiggerPlanError) &&
                <div
                    role="alert"
                    className="msg-box mt-4">
                    <p>You are downgrading your Voice ReGen subscription: the new plan will replace your current plan. Your existing unused credits will be saved.</p>
                </div>
            }

        </>
    )
};

export default ConfirmOrderBtnWithoutLeftSide;